/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { conversationsService, type ConversationListParams } from '../../services/conversations.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { ProjectCell } from '../../components/common/project-cell';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import type { Conversation } from '../../types/conversation.types';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import { formatDate } from 'sharedUi/dateUtils';
import NewConversationDialog from './new-conversation-dialog';

type ConversationRow = Conversation & { id: string };

/** 40×40 keeps every row action above the desktop hit-area floor. */
const rowAction = 'ui-press flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700';

const ConversationsList = ({ embedded = false }: { embedded?: boolean }) => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.CONVERSATIONS);
  const canDelete = useCanDelete(JOB_NAMES.CONVERSATIONS);

  const [rows, setRows] = useState<ConversationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const canManageProjects = accessibleProjects.length > 1;
  useEffect(() => { projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([])); }, []);

  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const debouncedSearchText = useDebounce(searchText, 500);
  const showError = useCallback((m: string) => toast.current?.show({ severity: 'error', summary: 'Error', detail: m, life: 10000 }), []);

  const fetchRows = useCallback(async (page = 1, size = pageSize, sortBy = sortField, order = sortOrder) => {
    try {
      setLoading(true);
      const params: ConversationListParams = { page, pageSize: size, includeDeleted: false };
      if (sortBy) { params.sortBy = sortBy; params.sortOrder = order === 1 ? 'asc' : 'desc'; }
      if (selectedProjectId) params.appId = selectedProjectId;
      const response = await conversationsService.list(params);
      let data = response.data || [];
      // The list has no server-side free-text search; filter the page by title client-side.
      const q = debouncedSearchText?.trim().toLowerCase();
      if (q) data = data.filter((c) => (c.title || '').toLowerCase().includes(q) || (c.agentCode || '').toLowerCase().includes(q));
      setRows(data.map((c) => ({ ...c, id: c._id })));
      setTotalRecords(response.pagination?.totalItems ?? response.total ?? data.length);
    } catch (e) { showError(extractErrorMessage(e, 'Failed to load conversations')); } finally { setLoading(false); }
  }, [debouncedSearchText, selectedProjectId, pageSize, sortField, sortOrder, showError]);

  const handlePageChange = (e: any) => { setFirst(e.first); setPageSize(e.rows); fetchRows(Math.floor(e.first / e.rows) + 1, e.rows, sortField, sortOrder); };
  const handleSort = (e: any) => { setSortField(e.sortField); setSortOrder(e.sortOrder); setFirst(0); fetchRows(1, pageSize, e.sortField, e.sortOrder); };
  useEffect(() => { setFirst(0); fetchRows(1, pageSize); }, [debouncedSearchText, selectedProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteConfirm = async () => {
    if (!selectedId) return;
    try { setDeleteLoading(true); await conversationsService.softDelete(selectedId); setDeleteDialogOpen(false); setSelectedId(null); fetchRows(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder); }
    catch (e: any) { setDeleteDialogOpen(false); setSelectedId(null); showError(extractErrorMessage(e, 'Failed to delete the conversation')); }
    finally { setDeleteLoading(false); }
  };

  const open = (id: string) => navigate(`/conversations/${id}`);

  const columns: SaasColumn<ConversationRow>[] = useMemo(() => [
    { field: 'title', header: 'Title', sortable: true, hideable: false, body: (c) => (
      <button type="button" className="text-left text-primary hover:underline" onClick={() => open(c._id)}>
        {c.title || 'Untitled conversation'}
      </button>
    ) },
    { field: 'agentCode', header: 'Agent', body: (c) => <span className="font-mono text-xs">{c.agentCode}</span> },
    { field: 'messageCount', header: 'Messages', align: 'right', body: (c) => <span className="tabular-nums">{c.messageCount ?? 0}</span> },
    { field: 'updatedAt', header: 'Updated At', sortable: true, body: (c) => <span className="tabular-nums">{formatDate(c.lastMessageAt || c.updatedAt || c.createdAt)}</span> },
    { field: 'appId', header: 'Project', body: (c: any) => <ProjectCell appId={c.appId} projects={accessibleProjects} /> },
    { field: 'actions', header: 'Actions', align: 'center', hideable: false, body: (c) => (
      <div className="flex justify-center gap-0.5">
        <button type="button" className={rowAction} onClick={() => open(c._id)} data-pr-tooltip="Open" aria-label="Open conversation"><i className="pi pi-comment text-emerald-600 dark:text-emerald-400 text-[1.05rem]" aria-hidden /></button>
        {canDelete && <button type="button" className={rowAction} onClick={() => { setSelectedId(c._id); setDeleteDialogOpen(true); }} data-pr-tooltip="Delete" aria-label="Delete conversation"><i className="pi pi-trash ui-act--delete text-[1.05rem]" aria-hidden /></button>}
      </div>
    ) },
  ], [canDelete, accessibleProjects]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PermissionGuard jobName={JOB_NAMES.CONVERSATIONS}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-Conversation-List">
        <PageHeader embedded={embedded} icon="pi pi-comments" title="Conversations"
          subtitle="Multi-turn chats with your conversational agents" testId="Conversation.Header" />
        <div className={embedded ? '' : 'ui-panel overflow-hidden'}>
          <SaasDataTable tableId="conversations" columns={columns} data={rows} loading={loading} dataKey="_id" emptyMessage="No conversations yet — start one to chat with an agent"
            first={first} rows={pageSize} totalRecords={totalRecords} onPage={handlePageChange} rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined} sortOrder={sortOrder || undefined} onSort={handleSort} onFilter={() => {}}
            globalSearch={searchText} onGlobalSearchChange={setSearchText} globalSearchPlaceholder="Search conversations..." testId="ConversationList"
            toolbarStart={<div className="flex items-center gap-3 flex-wrap">
              {canManageProjects && <Dropdown value={selectedProjectId} options={accessibleProjects.map((p) => ({ label: p.appCode ? `${p.name} (${p.appCode})` : p.name, value: p._id }))} onChange={(e: any) => setSelectedProjectId(e.value || null)} placeholder="Project" showClear style={{ minWidth: '160px' }} />}
            </div>}
            toolbarEnd={<><span className="whitespace-nowrap text-sm tabular-nums text-ink-muted">{loading && !rows.length ? 'loading…' : `${totalRecords} ${totalRecords === 1 ? 'record' : 'records'}`}</span>
              <RefreshButton onClick={() => fetchRows(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)} loading={loading} />
              {canCreate && <span className="ui-press inline-flex"><Button label="New Conversation" icon="pi pi-plus" onClick={() => setNewOpen(true)} size="small" /></span>}</>} />
        </div>
        <DeleteConfirmDialog visible={deleteDialogOpen} onCancel={() => { setDeleteDialogOpen(false); setSelectedId(null); }} onDelete={handleDeleteConfirm}
          message="Delete this conversation? Its messages are hidden too, and can be restored." testId="ConversationList.Dialog.Delete" loading={deleteLoading} />
        <NewConversationDialog visible={newOpen} projects={accessibleProjects} onClose={() => setNewOpen(false)}
          onCreated={(c) => { setNewOpen(false); navigate(`/conversations/${c._id}`); }} />
      </div>
    </PermissionGuard>
  );
};

export default ConversationsList;
